import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Media } from '@/js/libs/ui';
import { Button, Icons, Link } from '@/js/libs/ui/src/V2';
import { not, PrivateContent, or, Roles } from '@/js/libs/use-auth';

import { DEV_MODE } from 'src/constants';
import { useNavigationContext } from '../../context';
import { useNavigationComponentsVisibilityChecks } from '../../hooks/useNavigationComponentsVisibilityChecks';
import { LogoLarge, LogoSmall } from './Logo';
import { NotificationsButton } from './NotificationsButton';
import { DevBadge, LogoMenuGroup, ToggleButton, ToggleButtonWrapper, Wrapper } from './styled';

type Props = {
  onNeedSearchAutofocus?: (needAutoFocus: boolean) => void;
};

export const Header = memo(function Header({ onNeedSearchAutofocus }: Props) {
  const [t] = useTranslation(['common']);
  const { colors } = useTheme();
  const isDesktop = useMediaQuery({ query: Media.lgMin });

  const { isGlobalSearchVisible, isCompanySwitchVisible } = useNavigationComponentsVisibilityChecks();
  const { collapseMainNav, isCollapsedNav } = useNavigationContext();

  const onSearchClick = useCallbackRef(() => {
    onNeedSearchAutofocus?.(true);
    collapseMainNav();
  });

  return (
    <Wrapper $isCollapsedNav={isCollapsedNav} $isCompanySwitchView={isCompanySwitchVisible}>
      <LogoMenuGroup>
        <ToggleButtonWrapper $isCollapsedNav={isCollapsedNav}>
          {DEV_MODE && <DevBadge xs warning title={t('common:DEV_BADGE_TITLE')} $isCollapsedNav={isCollapsedNav} />}
          <ToggleButton onClick={collapseMainNav} $isCollapsedNav={isCollapsedNav}>
            <Icons.Menu size={24} active={isCollapsedNav} />
            {isCollapsedNav && isDesktop && <LogoSmall />}
          </ToggleButton>
          {(!isCollapsedNav || !isDesktop) && (
            <>
              <PrivateContent roles={or(Roles.BOOK_CAMPAIGNS, Roles.AGENCY, Roles.POSTADMANAGER)}>
                <Link to='/' tabIndex={-1}>
                  <LogoLarge />
                </Link>
              </PrivateContent>
              <PrivateContent roles={not(or(Roles.BOOK_CAMPAIGNS, Roles.AGENCY, Roles.POSTADMANAGER))}>
                <LogoLarge />
              </PrivateContent>
            </>
          )}
        </ToggleButtonWrapper>
      </LogoMenuGroup>

      <NotificationsButton />

      {isDesktop && isCollapsedNav && isGlobalSearchVisible && (
        <Button iconOnly ghost onClick={onSearchClick}>
          <Icons.Search color={colors.grey100_1} />
        </Button>
      )}
    </Wrapper>
  );
});
